import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import type { DocumentEditorRef, DocumentEditorProps } from '../composer/EditorContracts';
import type { DocumentFontType } from '../edgedocs/EdgeDocsTypes';
import { EdgeDocsVirtualPagesV3 } from './EdgeDocsVirtualPages.V3';
import type { DocumentService } from '../../core/services/DocumentService';
import { DPIPersistenceService } from '../../core/utils/DPIPersistenceService';

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false;
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

// ============================================================================
// ✅ PHASE 1: Removed useDebouncedCallback - no longer needed
// React's automatic batching handles rapid updates efficiently
// ============================================================================

/**
 * EdgeDocs Editor V3 - Minimal editor shell
 *
 * The simplest possible EdgeDocs editor that just renders a white paper.
 * Implements the basic DocumentEditorRef contract for compatibility.
 */
const EdgeDocsEditorV3 = forwardRef<DocumentEditorRef, DocumentEditorProps>(
  function EdgeDocsEditorV3(props: DocumentEditorProps, ref: React.Ref<DocumentEditorRef>) {
  // 🔬 DEBUG: Track instance identity to prove mount/unmount vs effect churn
  const instanceId = React.useRef(crypto.randomUUID().slice(0, 8));
  React.useEffect(() => {
    console.log(`🔬 [EdgeDocsEditorV3] MOUNTED instance=${instanceId.current}`);
    return () => {
      console.log(`🔬 [EdgeDocsEditorV3] UNMOUNTED instance=${instanceId.current}`);
    };
  }, []);

  const [documentText, setDocumentText] = useState('');
  // ✅ PERFORMANCE OPTIMIZATION: Optimistic text for immediate visual feedback
  const [optimisticText, setOptimisticText] = useState('');
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  const [astHash, setAstHash] = useState<string | undefined>(undefined);  // 🆕 Server-computed hash for cache
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 🔬 DEBUG: Track isLoading changes
  React.useEffect(() => {
    console.log(`🔬 [EdgeDocsEditorV3] isLoading changed to: ${isLoading}, instance=${instanceId.current}`);
  }, [isLoading]);
  
  // ✅ CRITICAL FIX: Declare documentStateRef early so it can be used in useImperativeHandle
  // This ref stores the current document state (optimistic text, page setup, font settings)
  // and is updated in a useEffect to keep it in sync with state changes
  const documentStateRef = React.useRef<{
    text: string;
    pageSetup?: any;
    fontSettings?: any;
    paragraphs?: Array<{ id: string; type: string; version: number; textStart: number; textEnd: number }>;
  }>({
    text: optimisticText,
    pageSetup: undefined,
    fontSettings: undefined,
    paragraphs: undefined
  });
  
  // 🔧 FIX: Store loaded paragraphs from AST for WASM initialization
  const [loadedParagraphs, setLoadedParagraphs] = React.useState<Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
  }> | undefined>(undefined);
  
  // 🆕 Ref to store the getDocumentParagraphs function from SharedInputContext
  // This is set by the VirtualPages component when it mounts
  const getDocumentParagraphsRef = React.useRef<(() => Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
  }>) | null>(null);

  // 🆕 Ref to store undo/redo handlers from SharedInputContext
  const undoRedoHandlersRef = React.useRef<{
    undo: () => Promise<void>;
    redo: () => Promise<void>;
    canUndo: boolean;
    canRedo: boolean;
  } | null>(null);

  debugLog(`📝 [EdgeDocsEditorV3] Initialized for doc: ${props.docId}`);

  // ✅ PERFORMANCE OPTIMIZATION: Sync optimistic and canonical text states
  const syncOptimisticText = useCallback((text: string) => {
    setOptimisticText(text);
  }, []);

  // ✅ PERFORMANCE OPTIMIZATION: Sync optimistic text when canonical text changes externally
  // (e.g., collaboration edits, document reload) to maintain consistency
  const previousDocumentTextRef = React.useRef(documentText);
  useEffect(() => {
    const documentTextChanged = documentText !== previousDocumentTextRef.current;
    if (documentTextChanged && documentText !== optimisticText) {
      DEBUG_ENABLED && console.log(`📝 [EdgeDocsEditorV3] Syncing optimistic text with canonical text:`, {
        canonicalLength: documentText.length,
        optimisticLength: optimisticText.length,
        source: 'external change'
      });
      setOptimisticText(documentText);
    }
    previousDocumentTextRef.current = documentText;
  }, [documentText, optimisticText]);

  // ✅ PHASE 1: Removed debouncing - immediate state updates
  // React's automatic batching handles rapid updates efficiently
  // 
  // ✅ CRITICAL FIX: Wrap in useCallback to prevent identity churn
  // Without useCallback, this function is recreated on every render, which:
  // 1. Changes onDocumentTextChange prop in VirtualPagesV3
  // 2. Causes RowComponent to be recreated (it's in useCallback deps)
  // 3. react-window sees new rowComponent → unmounts/remounts all rows
  // 4. Canvas unmounts → cursor resets to 0
  //
  // Using refs for values that change frequently (documentText, etc.) to avoid
  // adding them to the dependency array while still accessing current values.
  const handleDocumentTextChange = React.useCallback((newText: string) => {
    // Access current values via refs to avoid stale closures without adding dependencies
    const currentDocumentText = documentStateRef.current?.text || '';
    
    DEBUG_ENABLED && console.log(`📝 [EdgeDocsEditorV3] handleDocumentTextChange called:`, {
      newTextLength: newText.length,
      currentTextLength: currentDocumentText.length,
      textSample: newText.substring(0, 50)
    });

    // ✅ CRITICAL FIX: ALWAYS update ref synchronously so getDocumentState has latest text
    // This is essential for save operations - without this, content gets lost!
    // Don't wait for useEffect - update immediately to prevent stale data during save
    documentStateRef.current = {
      ...documentStateRef.current,
      text: newText
    };

    // ✅ PERFORMANCE FIX: Skip React state updates if text length hasn't changed significantly
    // Small changes (single character) don't need to trigger full re-renders
    // The ref update above ensures save will work correctly
    // Only trigger state updates for significant changes (page breaks, paste, etc.)
    const lengthDelta = Math.abs(newText.length - currentDocumentText.length);
    const isSignificantChange = lengthDelta > 10 || newText.includes('\f') !== currentDocumentText.includes('\f');
    
    if (!isSignificantChange && newText !== currentDocumentText) {
      // Small change - just update ref (already done above), skip state updates
      // 🔬 FIX: Do NOT call onDirtyChange here! It triggers setDirty(true) in DocumentOrchestrator,
      // which causes a full React re-render, unmounting/remounting the Canvas and causing visual doubling.
      // The dirty flag will be set on significant changes or on save.
      DEBUG_ENABLED && console.log(`📝 [EdgeDocsEditorV3] Small change, ref updated, skipping ALL state updates for performance`);
      return;
    }

    // ✅ IMMEDIATE: Update optimistic text for instant visual feedback
    setOptimisticText(newText);

    // ✅ IMMEDIATE: Update canonical document text state (no debounce)
    // Use functional update to avoid stale closure issue
    setDocumentText(prevText => {
      if (newText !== prevText) {
        DEBUG_ENABLED && console.log(`📝 [EdgeDocsEditorV3] Applying immediate document text update:`, {
          newTextLength: newText.length,
          oldTextLength: prevText.length,
          textSample: newText.substring(0, 50)
        });
        return newText;
      }
      return prevText;
    });

    // ✅ IMMEDIATE: Mark document as dirty (affects UI like save button)
    props.onDirtyChange?.(true);
  }, [props.onDirtyChange]); // Only depends on onDirtyChange callback identity

  // ✅ Merge page setup: override from dialog takes precedence over document metadata
  const effectivePageSetup = props.pageSetupOverride || documentMetadata?.pageSetup;
  debugLog(`📝 [EdgeDocsEditorV3] Page setup source:`, {
    hasOverride: !!props.pageSetupOverride,
    hasDocumentMetadata: !!documentMetadata?.pageSetup,
    using: props.pageSetupOverride ? 'Dialog Override' : 'Document Metadata',
    effectivePageSetup
  });

  // ✅ FONT SWITCHING FIX: Compute effective font settings
  // Toolbar props (user changes) take precedence over document metadata
  const effectiveFontSettings = React.useMemo(() => {
    const toolbarFont = props.fontFamilyKey;
    const toolbarWeight = props.fontWeight;
    const toolbarStyle = props.fontStyle;
    const toolbarSize = props.fontSizePx;
    const toolbarLineHeight = props.lineHeightMul;

    const docFont = documentMetadata?.fontSettings?.fontFamily?.replace(/'/g, '') || 'Inter';
    const docWeight = documentMetadata?.fontSettings?.fontWeight || 400;
    const docStyle = documentMetadata?.fontSettings?.fontStyle || 'normal';
    const docSize = documentMetadata?.fontSettings?.fontSizePx || 14;
    const docLineHeight = documentMetadata?.fontSettings?.lineHeightMul || 1.4;

    // Toolbar props override document metadata (user changed font via toolbar)
    // Font resolution is now handled by FontResolver - no manual mapping needed
    let fontFamily = toolbarFont || docFont;

    const fontWeight = toolbarWeight !== undefined ? toolbarWeight : docWeight;
    const fontStyle = toolbarStyle || docStyle;
    const fontSizePx = toolbarSize !== undefined ? toolbarSize : docSize;
    const lineHeightMul = toolbarLineHeight !== undefined ? toolbarLineHeight : docLineHeight;

    DEBUG_ENABLED && console.log('🎨 [EdgeDocsEditorV3] Effective font settings:', {
      toolbar: { font: toolbarFont, weight: toolbarWeight, style: toolbarStyle, size: toolbarSize, lineHeight: toolbarLineHeight },
      document: { font: docFont, weight: docWeight, style: docStyle, size: docSize, lineHeight: docLineHeight },
      effective: { fontFamily, fontWeight, fontStyle, fontSizePx, lineHeightMul },
      mapped: fontFamily !== (toolbarFont || docFont) ? `Mapped to: ${fontFamily}` : 'No mapping needed'
    });

    return {
      fontFamily,
      fontWeight,
      fontStyle,
      fontSizePx,
      lineHeightMul
    };
  }, [
    props.fontFamilyKey,
    props.fontWeight,
    props.fontStyle,
    props.fontSizePx,
    props.lineHeightMul,
    documentMetadata?.fontSettings
  ]);

  // ✅ CRITICAL FIX: Notify DocumentOrchestrator of document font settings for toolbar sync
  // This ensures the toolbar reflects the document's stored font, not just system defaults
  React.useEffect(() => {
    DEBUG_ENABLED && console.log('🔄 [EdgeDocsEditorV3] useEffect triggered:', {
      hasDocumentMetadata: !!documentMetadata,
      hasFontSettings: !!documentMetadata?.fontSettings,
      hasCallback: !!props.onFontSettingsLoaded,
      fontSettings: documentMetadata?.fontSettings
    });

    if (documentMetadata?.fontSettings && props.onFontSettingsLoaded) {
      const docSettings = documentMetadata.fontSettings;
      DEBUG_ENABLED && console.log('🔄 [EdgeDocsEditorV3] Notifying DocumentOrchestrator of document font settings:', docSettings);

      props.onFontSettingsLoaded({
        fontFamily: docSettings.fontFamily?.replace(/'/g, '') as DocumentFontType || 'Inter',
        fontSizePx: docSettings.fontSizePx || 14,
        lineHeightMul: docSettings.lineHeightMul || 1.4,
        fontWeight: docSettings.fontWeight || 400,
        fontStyle: docSettings.fontStyle || 'normal'
      });
    }
  }, [documentMetadata?.fontSettings, props.onFontSettingsLoaded]);

  // Initialize DPI service for render-time scaling
  useEffect(() => {
    DPIPersistenceService.enableCrossTabSync();
  }, []);

  // Load document content when component mounts
  useEffect(() => {
    // ✅ FIX: Guard against empty docId to prevent invalid API calls
    // Also check if docId is a valid UUID format (basic validation)
    const isValidDocId = props.docId && props.docId.trim() !== '' && props.docId.length > 10;
    
    if (!isValidDocId) {
      console.warn(`⚠️ [EdgeDocsEditorV3] docId is invalid or empty, skipping AST load:`, {
        docId: props.docId,
        docIdType: typeof props.docId,
        docIdLength: props.docId?.length
      });
      setIsLoading(false);
      return;
    }
    
    const loadDocumentContent = async () => {
      try {
        // ✅ FIX: Double-check docId is still valid before making API call
        if (!props.docId || props.docId.trim() === '') {
          console.error(`❌ [EdgeDocsEditorV3] docId became empty before API call, aborting`);
          setIsLoading(false);
          return;
        }
        
        debugLog(`📝 [EdgeDocsEditorV3] Loading document content from AST for: ${props.docId}, version: ${props.versionId || 'latest'}`);
        const content = await props.services.document.getDocumentContentFromAst(props.docId, props.versionId);
        setDocumentText(content.text);
        setOptimisticText(content.text); // ✅ Initialize optimistic text with document content
        setDocumentMetadata(content.metadata);
        
        // 🆕 Store server-computed AST hash for cache key
        if (content.astHash) {
          setAstHash(content.astHash);
          console.log(`💾 [EdgeDocsEditorV3] Server astHash: ${content.astHash.substring(0, 16)}...`);
        }
        
        // 🔧 FIX: Store paragraphs from AST for WASM initialization
        // Without this, WASM treats the entire document as one paragraph
        // 🔬 ALWAYS-ON DIAGNOSTIC: Track paragraph loading to debug save/load issues
        console.log('📊 [EdgeDocsEditorV3] Paragraphs from AST:', {
          hasParagraphs: !!content.paragraphs,
          paragraphCount: content.paragraphs?.length || 0,
          paragraphBoundaries: content.paragraphs?.map(p => `${p.id}: [${p.textStart}, ${p.textEnd})`) || []
        });
        
        if (content.paragraphs && content.paragraphs.length > 0) {
          setLoadedParagraphs(content.paragraphs);
          // 🔧 CRITICAL FIX: Update documentStateRef SYNCHRONOUSLY before setIsLoading(false)
          // Without this, EdgeDocsVirtualPagesV3 renders before useEffect updates the ref,
          // causing WASM to initialize with empty paragraphs array (single paragraph fallback)
          documentStateRef.current = {
            ...documentStateRef.current,
            text: content.text,
            paragraphs: content.paragraphs
          };
          console.log(`📝 [EdgeDocsEditorV3] ✅ Stored ${content.paragraphs.length} paragraphs for WASM init (sync)`);
        } else {
          console.warn('⚠️ [EdgeDocsEditorV3] No paragraphs from AST - WASM will use single paragraph fallback');
        }
        
        setIsLoading(false);
        debugLog(`📝 [EdgeDocsEditorV3] Document content loaded: ${content.text.length} characters`);
        debugLog(`📝 [EdgeDocsEditorV3] Document metadata:`, content.metadata);

        if (content.text.length === 0) {
          debugLog(`📝 [EdgeDocsEditorV3] Document is empty (normal for new documents)`);
        }

        // Load the active font to ensure worker has font bytes cached for glyph operations
        await loadActiveFont();

      } catch (error) {
        console.error(`📝 [EdgeDocsEditorV3] Failed to load document content:`, error);
        // Fallback to empty document
        setDocumentText('');
        setIsLoading(false);
      }
    };

    loadDocumentContent();
  }, [props.docId, props.versionId, props.services.document]);

  // Load active font to cache bytes in worker for glyph operations
  // ✅ PRIORITY 2 FIX: Use font from document metadata instead of hardcoded "Inter"
  const loadActiveFont = async () => {
    try {
      debugLog(`📝 [EdgeDocsEditorV3] Loading active font for glyph operations...`);

      // First, ensure font resolver cache is warmed (loads enabled fonts from API)
      const { fontWarmupService } = await import('../../services/FontWarmupService');
      const { fontResolver } = await import('../../services/FontResolver');

      debugLog(`📝 [EdgeDocsEditorV3] Warming font resolver...`);
      try {
        await fontWarmupService.warmup();
        debugLog(`📝 [EdgeDocsEditorV3] ✅ Font resolver warmed`);
      } catch (warmupError) {
        console.error(`📝 [EdgeDocsEditorV3] ❌ Font warmup failed:`, warmupError);
        debugLog(`📝 [EdgeDocsEditorV3] Continuing with fallback fonts...`);
      }

      // ✅ PRIORITY 2: Get font from effective font settings (toolbar props override document metadata)
      const fontFamily = effectiveFontSettings.fontFamily;
      const fontSizePx = effectiveFontSettings.fontSizePx;
      
      debugLog(`📝 [EdgeDocsEditorV3] Using font from document metadata:`, {
        fontFamily,
        fontSizePx,
        hasMetadata: !!documentMetadata,
        hasFontSettings: !!documentMetadata?.fontSettings
      });

      // Resolve font UID using FontResolver (handles case normalization)
      const resolvedFontUid = fontResolver.resolveFontUidSync(fontFamily, 400, 'normal');
      
      debugLog(`📝 [EdgeDocsEditorV3] Resolved font UID:`, {
        fontFamily,
        resolvedFontUid
      });

      // Import WebFontLoader dynamically to avoid circular dependencies
      const { WebFontLoader } = await import('../../rendering/fonts/WebFontLoader');

      // Load the font from document metadata (or fallback to Inter)
      const fontLoaded = await WebFontLoader.loadFont({
        font_uid: resolvedFontUid,
        family: fontFamily,
        weight: 400,
        style: 'normal'
      });

      if (fontLoaded) {
        debugLog(`📝 [EdgeDocsEditorV3] ✅ Font loaded and cached in worker: ${fontFamily} (${resolvedFontUid})`);
      } else {
        console.warn(`📝 [EdgeDocsEditorV3] ⚠️ Font loading completed but may not be ready`);
      }

      // Wait for font readiness (document.fonts.ready ensures FontFace is loaded)
      await document.fonts.ready;
      debugLog(`📝 [EdgeDocsEditorV3] ✅ Font faces ready for canvas rendering`);

    } catch (error) {
      console.error(`📝 [EdgeDocsEditorV3] ❌ Failed to load active font:`, error);
    }
  };

    // Implement DocumentEditorRef contract
    useImperativeHandle(ref, () => ({
      getSelection: () => ({
        anchor: { page: 0 as any, x: 0 as any, y: 0 as any },
        focus: { page: 0 as any, x: 0 as any, y: 0 as any },
        isCollapsed: true,
        text: '',
        pagesInvolved: 1,
        charCount: 0,
        wordCount: 0,
        start: { page: 0 as any, x: 0 as any, y: 0 as any },
        end: { page: 0 as any, x: 0 as any, y: 0 as any }
      }),

      setSelection: (selection: any) => {
        debugLog(`📝 [EdgeDocsEditorV3] setSelection called:`, selection);
      },

      applyDelta: (delta: any) => {
        debugLog(`📝 [EdgeDocsEditorV3] applyDelta called:`, delta);
        return Promise.resolve();
      },

      undo: () => {
        debugLog(`📝 [EdgeDocsEditorV3] undo called`);
        if (undoRedoHandlersRef.current?.undo) {
          undoRedoHandlersRef.current.undo();
        }
      },

      redo: () => {
        debugLog(`📝 [EdgeDocsEditorV3] redo called`);
        if (undoRedoHandlersRef.current?.redo) {
          undoRedoHandlersRef.current.redo();
        }
      },

      scrollTo: (position: any) => {
        debugLog(`📝 [EdgeDocsEditorV3] scrollTo called:`, position);
      },

      // ✅ NEW: Get current document state for saving
      getDocumentState: () => {
        // ✅ CRITICAL FIX: Read directly from ref.current which is updated synchronously in handleDocumentTextChange
        // This ensures we always get the latest text, even if called immediately after typing
        const currentRefState = documentStateRef.current;
        // Fallback to optimisticText if ref is somehow stale (shouldn't happen, but safety check)
        const currentText = currentRefState?.text || optimisticText || documentText;
        
        // 🆕 Get paragraph metadata from WASM via SharedInputContext
        // 🔬 DIAGNOSTIC: Check if the getter ref is set
        console.log('🔬 [EdgeDocsEditorV3] getDocumentState - checking paragraph getter:', {
          hasGetterRef: !!getDocumentParagraphsRef.current,
          getterType: typeof getDocumentParagraphsRef.current
        });
        
        const paragraphs = getDocumentParagraphsRef.current?.() || undefined;
        
        // 🔬 DIAGNOSTIC: Always log (not just DEBUG_ENABLED)
        console.log(`💾 [EdgeDocsEditorV3] getDocumentState called:`, {
          textLength: currentText.length,
          textSample: currentText.substring(0, 50),
          source: currentRefState?.text ? 'documentStateRef.current' : (optimisticText ? 'optimisticText' : 'documentText'),
          refTextLength: currentRefState?.text?.length || 0,
          optimisticTextLength: optimisticText.length,
          localTextLength: documentText.length,
          paragraphCount: paragraphs?.length || 0,
          paragraphs: paragraphs // 🔬 Log the actual paragraphs array
        });
        return {
          text: currentText,
          paragraphs
        };
      },

      dispose: () => {
        debugLog(`📝 [EdgeDocsEditorV3] dispose called`);
      }
    }), [documentText, optimisticText]);

    // Notify parent about status
    useEffect(() => {
      props.onStatus?.({
        kind: 'info',
        msg: 'EdgeDocs V3 editor loaded'
      });
    }, [props.onStatus]);

    // ✅ PERFORMANCE OPTIMIZATION: Use optimistic text for immediate visual feedback
    // Keep object reference stable to prevent unnecessary canvas re-renders
    // Update the ref content without changing the reference
    React.useEffect(() => {
      documentStateRef.current = {
        text: optimisticText,
        pageSetup: effectivePageSetup,
        fontSettings: effectiveFontSettings,
        paragraphs: loadedParagraphs  // 🔧 FIX: Include paragraphs for WASM initialization
      };
    }, [optimisticText, effectivePageSetup, effectiveFontSettings, loadedParagraphs]);

    // 🔧 CRITICAL FIX: Construct documentState from current state values, NOT from ref
    // The ref is updated in useEffect (which runs AFTER render), so reading from ref
    // during render gives stale values. This caused WASM to initialize without paragraphs
    // because loadedParagraphs wasn't in documentStateRef.current yet on first render.
    //
    // By using useMemo with the actual state values as dependencies, we ensure
    // documentState has the correct paragraphs when passed to VirtualPagesV3.
    const documentState = React.useMemo(() => ({
      text: optimisticText,
      pageSetup: effectivePageSetup,
      fontSettings: effectiveFontSettings,
      paragraphs: loadedParagraphs
    }), [optimisticText, effectivePageSetup, effectiveFontSettings, loadedParagraphs]);

    return (
      <div
        ref={containerRef}
        className="edgedocs-editor-v3"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: '#f5f5f5'
        }}
      >
        {/* Render the virtual pages (white paper) */}
        {/* ✅ FIX: Only pass documentState when loading is complete to avoid initializing with empty text */}
        {!isLoading && (
          <EdgeDocsVirtualPagesV3
            documentState={documentState}
            docId={props.docId}
            versionId={props.versionId}
            astHash={astHash}  // 🆕 Server-computed hash for persistent cache
            zoomMode={props.zoomMode}
            userZoom={props.userZoom}
            onWasmStatusChange={props.onWasmStatusChange}
            onDocumentTextChange={handleDocumentTextChange}  // ✅ Pass text change handler down
            onPageCountChange={props.onPageCountChange}  // 🔧 FIX: Pass page count callback for persistence
            onParagraphGetterReady={(getter) => {
              getDocumentParagraphsRef.current = getter;
              DEBUG_ENABLED && console.log('📊 [EdgeDocsEditorV3] Paragraph getter registered');
            }}
            onUndoRedoReady={(handlers) => {
              undoRedoHandlersRef.current = handlers;
              DEBUG_ENABLED && console.log('↩️ [EdgeDocsEditorV3] Undo/Redo handlers registered');
            }}
          />
        )}
      </div>
    );
  }
);

export default EdgeDocsEditorV3;
